import { useMemo, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { mockPrendas, mockLooksCalendario, mockRecomendaciones } from '../mocks/clienteArmario';
import { mockClientesAsesoria } from '../mocks/clientesAsesoria';
import type { Prenda } from './useAdminArmario';
import type { LookCalendario } from './useClienteCalendario';
import type { Recomendacion } from './useClienteRecomendaciones';

export interface ClienteAsesoriaData {
  id: number;
  nombre: string;
  email: string | null;
  telefono: string | null;
  plan_contratado: string | null;
  fecha_inicio: string | null;
  notas: string | null;
  activo: boolean;
}

export interface ClientePerfilStats {
  totalPrendas: number;
  prendasActivas: number;
  prendasInactivas: number;
  prendasPorCategoria: Record<string, number>;
  prendasMasUsadas: Prenda[];
  prendasMenosUsadas: Prenda[];
  totalLooks: number;
  looksPlaneados: number;
  looksUsados: number;
  looksCancelados: number;
  proximoLook: LookCalendario | null;
  ultimaRecomendacion: Recomendacion | null;
  recomendacionesPendientes: number;
  recomendacionesLeidas: number;
  diasDesdeInicio: number | null;
  categoriasPrincipales: string[];
  topColores: string[];
}

export function useClientePerfil(
  clienteId: number | undefined,
  prendas: Prenda[],
  looks: LookCalendario[],
  recomendaciones: Recomendacion[]
) {
  const [cliente, setCliente] = useState<ClienteAsesoriaData | null>(null);
  const [loadingCliente, setLoadingCliente] = useState(false);

  const fetchCliente = useCallback(async () => {
    if (!clienteId || clienteId <= 0) {
      setCliente(null);
      return;
    }
    setLoadingCliente(true);
    try {
      const { data: row, error: err } = await supabase
        .from('clientes_asesoria')
        .select('*')
        .eq('id', clienteId)
        .maybeSingle();
      if (err) {
        // Fallback to mock data
        const mock = mockClientesAsesoria.find((c) => c.id === clienteId) ?? null;
        setCliente(mock);
      } else if (row) {
        setCliente(row as ClienteAsesoriaData);
      } else {
        const mock = mockClientesAsesoria.find((c) => c.id === clienteId) ?? null;
        setCliente(mock);
      }
    } catch {
      const mock = mockClientesAsesoria.find((c) => c.id === clienteId) ?? null;
      setCliente(mock);
    } finally {
      setLoadingCliente(false);
    }
  }, [clienteId]);

  useEffect(() => {
    fetchCliente();
  }, [fetchCliente]);

  const stats = useMemo<ClientePerfilStats>(() => {
    const prendasCliente = prendas.filter((p) => p.cliente_id === clienteId);
    const looksCliente = looks.filter((l) => l.cliente_id === clienteId);
    const recCliente = recomendaciones.filter((r) => r.cliente_id === clienteId);

    const totalPrendas = prendasCliente.length;
    const prendasActivas = prendasCliente.filter((p) => p.activa).length;
    const prendasInactivas = totalPrendas - prendasActivas;

    const porCategoria: Record<string, number> = {};
    prendasCliente.forEach((p) => {
      porCategoria[p.categoria] = (porCategoria[p.categoria] || 0) + 1;
    });

    const sortedByUso = [...prendasCliente].sort((a, b) => b.frecuencia_uso - a.frecuencia_uso);
    const prendasMasUsadas = sortedByUso.slice(0, 5);
    const prendasMenosUsadas = sortedByUso.filter((p) => p.activa).slice(-5).reverse();

    const totalLooks = looksCliente.length;
    const looksPlaneados = looksCliente.filter((l) => l.estado === 'planeado').length;
    const looksUsados = looksCliente.filter((l) => l.estado === 'usado').length;
    const looksCancelados = looksCliente.filter((l) => l.estado === 'cancelado').length;

    const hoy = new Date();
    const proximoLook = [...looksCliente]
      .filter((l) => new Date(l.fecha) >= hoy && l.estado !== 'cancelado')
      .sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime())[0] ?? null;

    const sortedRec = [...recCliente].sort(
      (a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
    );
    const ultimaRecomendacion = sortedRec[0] ?? null;
    const recomendacionesPendientes = recCliente.filter((r) => !r.leida).length;
    const recomendacionesLeidas = recCliente.filter((r) => r.leida).length;

    const diasDesdeInicio = cliente?.fecha_inicio
      ? Math.floor((Date.now() - new Date(cliente.fecha_inicio).getTime()) / (1000 * 60 * 60 * 24))
      : null;

    const categoriasPrincipales = Object.entries(porCategoria)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([cat]) => cat);

    const colorMap: Record<string, number> = {};
    prendasCliente.forEach((p) => {
      colorMap[p.color] = (colorMap[p.color] || 0) + 1;
    });
    const topColores = Object.entries(colorMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([c]) => c);

    return {
      totalPrendas,
      prendasActivas,
      prendasInactivas,
      prendasPorCategoria: porCategoria,
      prendasMasUsadas,
      prendasMenosUsadas,
      totalLooks,
      looksPlaneados,
      looksUsados,
      looksCancelados,
      proximoLook,
      ultimaRecomendacion,
      recomendacionesPendientes,
      recomendacionesLeidas,
      diasDesdeInicio,
      categoriasPrincipales,
      topColores,
    };
  }, [clienteId, prendas, looks, recomendaciones, cliente]);

  return { cliente, stats, loadingCliente };
}
